import { useEffect, useRef, useState } from 'react';
import statsService from '../../services/statsService';

const IDLE = { data: null, error: null, isLoading: false };
const LOADING = { data: null, error: null, isLoading: true };

const hasValue = (value) =>
    value !== null && value !== undefined && value !== '';

/**
 * Forecast confidence of a campaign for a UTC range: the per-screen summary,
 * or one screen's hours when `screenId` is given. It reloads when the
 * campaign, range or screen changes, and drops responses for a previous one.
 * With `enabled` false, or without a campaign or range, it makes no request.
 * @returns {{ data: object|null, error: object|null, isLoading: boolean }}
 */
export default function useCampaignForecastConfidence({
    campaignId,
    from,
    to,
    screenId = null,
    requestConfig,
    enabled = true,
}) {
    const isReady =
        enabled && hasValue(campaignId) && hasValue(from) && hasValue(to);
    const [state, setState] = useState(isReady ? LOADING : IDLE);

    // The config object may be rebuilt on every render; only its token
    // should trigger a reload.
    const configRef = useRef(requestConfig);
    configRef.current = requestConfig;
    const authorization = requestConfig?.headers?.Authorization ?? '';

    useEffect(() => {
        if (!isReady) {
            setState(IDLE);
            return undefined;
        }
        setState(LOADING);

        let isCurrent = true;
        const params = {
            campaignId,
            from,
            to,
            requestConfig: configRef.current,
        };
        const request = hasValue(screenId)
            ? statsService.getCampaignForecastConfidenceScreen({
                  ...params,
                  screenId,
              })
            : statsService.getCampaignForecastConfidence(params);

        request.then(
            (data) => {
                if (isCurrent)
                    setState({ data, error: null, isLoading: false });
            },
            (error) => {
                if (isCurrent)
                    setState({ data: null, error, isLoading: false });
            }
        );
        return () => {
            isCurrent = false;
        };
    }, [isReady, campaignId, from, to, screenId, authorization]);

    return state;
}
